import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, CheckCheck, ChevronDown, ChevronUp, Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/dates";

export default function Notifications() {
  const [forReview, setForReview] = useState([]);
  const [digest, setDigest] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", {
        params: { limit: 200, grouped: true },
      });
      setForReview(data.for_review || []);
      setDigest(data.digest || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setForReview((r) => r.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setDigest((d) =>
        d.map((day) => ({
          ...day,
          items: day.items.map((n) => (n.id === id ? { ...n, read: true } : n)),
        })),
      );
      window.dispatchEvent(new CustomEvent("mypatria:notifications-updated"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      await load();
      window.dispatchEvent(new CustomEvent("mypatria:notifications-updated"));
      toast.success("All caught up.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const hasForReview = forReview.length > 0;
  const hasDigest = digest.length > 0;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">Inbox</p>
          <h1 className="text-4xl sm:text-5xl font-serif text-forest-900 mt-1">Notifications</h1>
          <p className="text-sm text-charcoal-muted mt-1">
            Urgent items surface first. Everything else is grouped into a gentle weekly digest.
          </p>
        </div>
        <button
          onClick={markAll}
          className="patria-btn-secondary min-h-[44px] self-start sm:self-auto"
          data-testid="notifications-mark-all"
        >
          <CheckCheck className="h-4 w-4" strokeWidth={1.5} /> Mark all as read
        </button>
      </div>

      {loading && <p className="italic text-charcoal-muted">Loading notifications...</p>}

      {!loading && !hasForReview && !hasDigest && total === 0 && (
        <div className="patria-card p-10 text-center">
          <BellRing className="h-8 w-8 mx-auto text-forest-600" strokeWidth={1.5} />
          <p className="mt-3 text-charcoal-soft">You&rsquo;re all caught up.</p>
        </div>
      )}

      {/* For your review — urgent + unread */}
      {!loading && hasForReview && (
        <section data-testid="notifications-review">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-terracotta-500" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-forest-900">For Your Review</h2>
            <span className="patria-chip-terra text-[10px]">{forReview.length}</span>
          </div>
          <ul className="space-y-3">
            {forReview.map((n) => (
              <NotificationRow key={n.id} n={n} onRead={markRead} />
            ))}
          </ul>
        </section>
      )}

      {/* Weekly digest — informational, grouped by day */}
      {!loading && hasDigest && (
        <section data-testid="notifications-digest">
          <div className="flex items-center gap-2 mb-3">
            <BellRing className="h-4 w-4 text-forest-600" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-forest-900">Weekly Digest</h2>
            <span className="text-xs text-charcoal-muted italic">Grouped so it stays gentle.</span>
          </div>
          <ul className="space-y-3">
            {digest.map((day) => {
              const isOpen = expandedDay === day.day;
              return (
                <li
                  key={day.day}
                  className="patria-card p-4"
                  data-testid={`digest-day-${day.day}`}
                >
                  <button
                    type="button"
                    className="w-full flex items-center justify-between text-left min-h-[44px]"
                    onClick={() => setExpandedDay(isOpen ? null : day.day)}
                    data-testid={`digest-toggle-${day.day}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-forest-900">
                        {day.day || "Recent"}
                      </p>
                      <p className="text-xs text-charcoal-muted">
                        {day.count} update{day.count === 1 ? "" : "s"}
                      </p>
                    </div>
                    {isOpen ? (
                      <ChevronUp className="h-4 w-4 text-forest-700" strokeWidth={1.5} />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-forest-700" strokeWidth={1.5} />
                    )}
                  </button>
                  {isOpen && (
                    <ul className="mt-3 space-y-2 border-t border-sage-300/60 pt-3">
                      {day.items.map((n) => (
                        <NotificationRow key={n.id} n={n} onRead={markRead} compact />
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}

function NotificationRow({ n, onRead, compact }) {
  return (
    <li
      className={`${compact ? "p-3 rounded-xl bg-cream-100" : "patria-card p-5"}`}
      data-testid={`notif-${n.id}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <span
          className={`mt-1 h-2.5 w-2.5 rounded-full shrink-0 ${
            n.read ? "bg-sage-300" : n.urgent ? "bg-terracotta-500" : "bg-forest-600"
          }`}
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-forest-900">{n.title}</p>
            <span className="patria-chip-green" data-testid={`notif-type-${n.id}`}>
              {n.type_label || (n.type || "").replaceAll("_", " ")}
            </span>
            {n.urgent && (
              <span className="patria-chip-terra text-[10px]">Urgent</span>
            )}
          </div>
          <p className="mt-1 text-sm text-charcoal-soft whitespace-pre-wrap">{n.body}</p>
          <p className="mt-1 text-xs text-charcoal-muted">{formatDateTime(n.created_at)}</p>
        </div>
        <div className="flex flex-col sm:flex-row items-end sm:items-start gap-1 sm:gap-2 shrink-0">
          {!n.read && (
            <button
              className="patria-btn-ghost text-xs min-h-[44px] min-w-[44px]"
              onClick={() => onRead(n.id)}
              data-testid={`notif-read-${n.id}`}
            >
              Mark read
            </button>
          )}
          {n.link && (
            <a
              href={n.link}
              className="patria-btn-ghost text-forest-700 text-xs min-h-[44px] min-w-[44px]"
              data-testid={`notif-open-${n.id}`}
            >
              Open
            </a>
          )}
        </div>
      </div>
    </li>
  );
}
